import { TutorialConfig, TutorialStep, TutorialTask } from '../types';

// Define the KiCad tutorial tasks
const KICAD_TASKS: TutorialTask[] = [
  {
    id: 'obvious-success',
    name: 'Obvious Success - Delete Wire',
    type: 'success',
    description: 'Learn to identify clear successful task completions',
    runId: 'aa5870ec-2c52-41ba-adab-7291dd099b93'
  },
  {
    id: 'obvious-failure',
    name: 'Obvious Failure - Ground Connection',
    type: 'failure',
    description: 'Learn to identify clear task failures',
    runId: 'f2a6e272-ab8c-4352-88a6-3af1512d4c20'
  },
  {
    id: 'hidden-failure',
    name: 'Hidden Failure - Cancel Button',
    type: 'fake_success',
    description: 'Learn to spot failures that look like successes',
    runId: 'c3202a20-0460-4e07-b49c-c1cd0247e3c1'
  },
  {
    id: 'hidden-success',
    name: 'Hidden Success - Messy but Correct',
    type: 'fake_failure',
    description: 'Learn to identify successes that look messy',
    runId: '1a3b3e55-8da8-4996-b421-17c0b3ceaeb1'
  }
];

// Platform navigation instructions (same for all software)
const PLATFORM_INTRO_STEPS: TutorialStep[] = [
  {
    id: "welcome",
    title: "Welcome to KiCad Evaluation!",
    content:
      "This tutorial will show you how to evaluate KiCad model runs. First, let's learn how to use the platform.",
    targetSelector: ".App-header h1",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-description",
    title: "Task Description",
    content:
      "This shows the instructions given to the AI model to complete inside KiCad. Read this carefully to understand what the model was asked to do.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "skip-to-frame-2",
    title: "Skip to Frame 2",
    content:
      "Click the right arrow button or press the RIGHT arrow key to go to the next frame.",
    targetSelector: ".nav-button:nth-child(3)",
    action: "seek",
    position: "top",
  },
  {
    id: "current-action",
    title: "Current Action",
    content:
      "This section describes what action the AI model is performing in the current frame. It helps you understand what the model is trying to do.",
    targetSelector: ".sidebar-section:nth-child(3)",
    action: "next",
    position: "right",
  },
  {
    id: "main-click",
    title: "Main Click Indicator",
    content:
      "The circle with ripple effect shows where the model clicked on the screen in this frame. This helps you track the model's interactions.",
    targetSelector: ".click-indicator",
    action: "next",
    position: "left",
  },
  {
    id: "next-click",
    title: "Next Click Preview",
    content:
      "The faded circle shows where the model will click in the next frame. This helps you anticipate the model's next action.",
    targetSelector: ".next-click-indicator",
    action: "next",
    position: "left",
  },
  {
    id: "go-to-frame-3",
    title: "Go to Next Frame",
    content:
      'Click the right arrow button to advance to frame 3. Notice how the previous "next click" becomes the current click.',
    targetSelector: ".nav-button:nth-child(3)",
    action: "seek",
    position: "top",
  },
  {
    id: "seek-backward",
    title: "Previous Frame",
    content:
      "Click the left arrow button or press the LEFT arrow key to go back to the previous frame.",
    targetSelector: ".nav-button:first-child",
    action: "seek",
    position: "top",
  },
  {
    id: "play",
    title: "Play the Video",
    content: "Click the play button or press SPACE to start playing the video.",
    targetSelector: ".play-pause-btn",
    action: "play",
    position: "top",
  },
  {
    id: "pause",
    title: "Pause the Video",
    content: "Click the pause button or press SPACE to pause the video.",
    targetSelector: ".play-pause-btn",
    action: "pause",
    position: "top",
  },
  {
    id: "speed-controls",
    title: "Speed Controls",
    content:
      "Use the speed buttons to control playback speed. This helps you carefully review complex actions.",
    targetSelector: ".speed-selector",
    action: "next",
    position: "top",
  },
  {
    id: "seek-timeline",
    title: "Timeline Scrubbing",
    content:
      "Click and drag on the timeline to quickly navigate to any frame. The blue bar shows your current position. Try it now, then click Next when ready.",
    targetSelector: ".timeline-container",
    action: "next",
    position: "top",
  },
  {
    id: "download-model",
    title: "Download KiCad File",
    content:
      "Right-click anywhere on the screenshot to download the KiCad ZIP file. This is crucial for verifying results that may not be visible in the screenshot.",
    targetSelector: ".screenshot-container",
    action: "next",
    position: "left",
  },
  {
    id: "watch-before-eval",
    title: "Watch Before Evaluating",
    content:
      "Important: You must watch the entire video before evaluating. Let's jump to the final frame to see how evaluation works.",
    targetSelector: ".sidebar-section:nth-child(2)",
    action: "next",
    position: "right",
  },
  {
    id: "finalframe",
    title: "Jump to Final Frame",
    content:
      "Click on the timeline near the end to jump to the final frame. This shows the end result of the AI's work.",
    targetSelector: ".timeline-container",
    action: "next",
    position: "top",
  }
];

// Define the tutorial steps for each task
const OBVIOUS_SUCCESS_STEPS: TutorialStep[] = [
  {
    id: 'obvious-success-intro',
    title: 'Example 1: Delete a Wire',
    content: 'The task asks the AI to delete a specific wire between components. Let\'s see how it performs.',
    targetSelector: '.task-description',
    action: 'next',
    position: 'right'
  },
  {
    id: 'obvious-success-video',
    title: 'Task Completed Successfully',
    content: 'The AI successfully deletes the wire as requested. It doesn\'t matter how many steps it took - what matters is that the task was completed correctly.',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left'
  },
  {
    id: 'obvious-success-label',
    title: 'Label as Success',
    content: 'When the AI model completes the task as requested, without making additional changes, mark it as a SUCCESS. Click the Success button.',
    targetSelector: '.evaluation-btn.success',
    action: 'evaluate',
    position: 'top',
    expectedEvaluation: 'success'
  },
  {
    id: 'obvious-success-complete',
    title: 'Correct!',
    content: 'That\'s right! The AI completed the task as requested. Click "Next task" to continue to the next example.',
    targetSelector: 'button.next-task',
    action: 'next-task-button',
    position: 'top'
  }
];

const OBVIOUS_FAILURE_STEPS: TutorialStep[] = [
  {
    id: 'obvious-failure-intro',
    title: 'Example 2: Connect to Ground',
    content: 'The task asks the AI to connect a component to ground. Let\'s watch how it attempts this.',
    targetSelector: '.task-description',
    action: 'next',
    position: 'right'
  },
  {
    id: 'obvious-failure-video',
    title: 'Failed Wire Drawing',
    content: 'The AI is nowhere close to drawing any wires properly. It cannot establish proper connections between components.',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left'
  },
  {
    id: 'obvious-failure-label',
    title: 'Label as Failure',
    content: 'When the AI fails to perform basic operations like this, it\'s clearly a FAILURE. Click the Failure button.',
    targetSelector: '.evaluation-btn.fail',
    action: 'evaluate',
    position: 'top',
    expectedEvaluation: 'fail'
  },
  {
    id: 'obvious-failure-complete',
    title: 'Correct!',
    content: 'You identified the failure correctly. Click "Next task" to continue.',
    targetSelector: 'button.next-task',
    action: 'next-task-button',
    position: 'top'
  }
];

const HIDDEN_FAILURE_STEPS: TutorialStep[] = [
  {
    id: 'hidden-failure-intro',
    title: 'Example 3: Edit Design Rules',
    content: 'The task asks the AI to set minimum clearance, track width, and through hole to 0.2mm in the board setup. Pay close attention to how it completes this task.',
    targetSelector: '.task-description',
    action: 'next',
    position: 'right'
  },
  {
    id: 'hidden-failure-watch',
    title: 'Watch Carefully!',
    content: 'The AI sets minimum clearance, track width, and through hole to 0.2mm as requested. But watch what happens at the end...',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left'
  },
  {
    id: 'hidden-failure-cancel',
    title: 'The Cancel Button!',
    content: 'Did you catch it? The AI clicked "Cancel" instead of "OK"! This means NONE of the changes were saved.',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left'
  },
  {
    id: 'hidden-failure-download',
    title: 'Download When Uncertain',
    content: 'If you\'re not sure if the model successfully completed the task, download the KiCad file and check!',
    targetSelector: '.download-section',
    action: 'next',
    position: 'left'
  },
  {
    id: 'hidden-failure-label',
    title: 'Label as Failure',
    content: 'Since the changes weren\'t saved (Cancel was clicked), this is a FAILURE. Click the Failure button.',
    targetSelector: '.evaluation-btn.fail',
    action: 'evaluate',
    position: 'top',
    expectedEvaluation: 'fail'
  },
  {
    id: 'hidden-failure-complete',
    title: 'Well Done!',
    content: 'Excellent! You caught that the changes weren\'t saved. Downloading files helps when you\'re uncertain! Click "Next task" to continue.',
    targetSelector: 'button.next-task',
    action: 'next-task-button',
    position: 'top'
  }
];

const HIDDEN_SUCCESS_STEPS: TutorialStep[] = [
  {
    id: 'hidden-success-intro',
    title: 'Example 4: Connect Two Pins',
    content: 'The task asks the AI to draw a wire connecting U1 pin 4 to U2 pin 5 in the schematic editor. Note: it specifically says "Do not make any other modifications."',
    targetSelector: '.task-description',
    action: 'next',
    position: 'right'
  },
  {
    id: 'hidden-success-messy',
    title: 'Extra Steps Don\'t Matter',
    content: 'The AI clicks "place power symbols" unnecessarily and takes a roundabout path. But efficiency doesn\'t determine success - only whether the task was completed as requested.',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left'
  },
  {
    id: 'hidden-success-netlist',
    title: 'Check the Netlist!',
    content: 'BUT - KiCad evaluations are based on NETLIST equivalence, not visual appearance! The connection from U1 pin 4 to U2 pin 5 is correctly established.',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left'
  },
  {
    id: 'hidden-success-remember',
    title: 'Remember the Rule',
    content: 'In KiCad schematics, what matters is that the requested connection was made (netlist), not how it looks or how many steps it took. This is a SUCCESS.',
    targetSelector: '.video-container',
    action: 'next',
    position: 'top'
  },
  {
    id: 'hidden-success-label',
    title: 'Label as Success',
    content: 'Since the netlist is correct (proper electrical connections), this is a SUCCESS. Click the Success button.',
    targetSelector: '.evaluation-btn.success',
    action: 'evaluate',
    position: 'top',
    expectedEvaluation: 'success'
  },
  {
    id: 'tutorial-complete',
    title: 'Tutorial Complete!',
    content: 'Congratulations! You\'ve learned to identify obvious successes/failures and tricky cases. Click below to start evaluating real KiCad tasks. Click on "New Projects" to claim your first labeling project.',
    action: 'next',
    position: 'bottom'
  }
];

// Combine all steps into a single array
const KICAD_STEPS: TutorialStep[] = [
  ...PLATFORM_INTRO_STEPS,
  ...OBVIOUS_SUCCESS_STEPS,
  ...OBVIOUS_FAILURE_STEPS,
  ...HIDDEN_FAILURE_STEPS,
  ...HIDDEN_SUCCESS_STEPS
];

// Create the final tutorial configuration
export const kicadTutorialConfig: TutorialConfig = {
  software: 'kicad',
  displayName: 'KiCad',
  totalTasks: KICAD_TASKS.length,
  tasks: KICAD_TASKS,
  steps: KICAD_STEPS,
  completionKey: 'kicad_labeling_tutorial_completed',
  dataSource: 'api'
  // Don't define fetchTaskData - let TutorialService handle API calls directly
};